// Cameras ordered by relevance to the event (design/README.md, agreed 2026-10-16): the event's alarm cameras (newest
// alarm first), then the cameras the agent cited (its evidence frames' cameras, and subjects whose location names a
// camera), then map neighbours by distance from the first alarm's camera, then the rest. Used by the quad view (the top
// four) and the pop-out camera matrix (all of them, each labelled with why it is there).

import type { AgentPanel, EventCamera, EventDetail } from '../api.ts';
import { alarmMs, camShort, plural } from './util.ts';

export type CamWhy =
  | { kind: 'alarm'; alarms: number; lastAt: number }
  | { kind: 'ai'; via: 'evidence' | 'subject' }
  | { kind: 'near'; of: string; nth: number }
  | { kind: 'rest' };

export interface RankedCam {
  cam: EventCamera;
  why: CamWhy;
  /** "Alarm · 2 alarms", "AI cited", "Near Cam 02 · 3rd", "Site camera". */
  label: string;
}

export function ordinal(n: number): string {
  const s = n % 100 >= 11 && n % 100 <= 13 ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] ?? 'th');
  return `${n}${s}`;
}

/** Digits of "Cam 02" as a number (2), or null. */
function camNo(name: string): number | null {
  const m = /\bcam(?:era)?\s*#?\s*0*(\d+)\b/i.exec(name);
  return m ? Number(m[1]) : null;
}

/** Does free text ("by the gate near Cam 2", "Showroom front") mention this camera? */
export function mentionsCamera(text: string, cam: EventCamera): boolean {
  const t = text.toLowerCase();
  const no = camNo(cam.name) ?? cam.displayNumber;
  if (no !== null && no !== undefined) {
    const re = /\bcam(?:era)?\s*#?\s*0*(\d+)\b/gi;
    for (let m = re.exec(t); m; m = re.exec(t)) if (Number(m[1]) === no) return true;
  }
  const desc = cam.name.split(' · ').slice(1).join(' · ').trim().toLowerCase();
  if (desc.length >= 4 && t.includes(desc)) return true;
  const loc = cam.locationDescription?.trim().toLowerCase();
  return !!loc && loc.length >= 4 && t.includes(loc);
}

/** Camera ids the agent's latest assessment cites: its evidence frames' cameras and cameras its subjects are near. */
export function aiCitedCameraIds(ev: EventDetail, panel: AgentPanel | null | undefined): Map<string, 'evidence' | 'subject'> {
  const out = new Map<string, 'evidence' | 'subject'>();
  if (!panel || !panel.visible) return out;
  const as = panel.assessment;
  for (const mid of as?.evidenceMediaIds ?? []) {
    const a = ev.alarms.find((x) => x.snapshotUrl?.endsWith(`/${mid}`));
    if (a?.cameraId && !out.has(a.cameraId)) out.set(a.cameraId, 'evidence');
  }
  for (const s of as?.subjects ?? []) {
    const text = [s.location, s.description].filter(Boolean).join(' · ');
    if (!text) continue;
    for (const cam of ev.cameras) if (!out.has(cam.id) && mentionsCamera(text, cam)) out.set(cam.id, 'subject');
  }
  return out;
}

export function rankCameras(ev: EventDetail, panel: AgentPanel | null | undefined): RankedCam[] {
  const byId = new Map(ev.cameras.map((c) => [c.id, c]));
  const out: RankedCam[] = [];
  const used = new Set<string>();

  // (a) alarm cameras, newest alarm first
  const alarmInfo = new Map<string, { n: number; last: number }>();
  for (const a of ev.alarms) {
    if (!a.cameraId) continue;
    const cur = alarmInfo.get(a.cameraId) ?? { n: 0, last: 0 };
    alarmInfo.set(a.cameraId, { n: cur.n + 1, last: Math.max(cur.last, alarmMs(a)) });
  }
  [...alarmInfo.entries()]
    .sort((x, y) => y[1].last - x[1].last)
    .forEach(([id, info]) => {
      const cam = byId.get(id);
      if (!cam) return;
      used.add(id);
      out.push({ cam, why: { kind: 'alarm', alarms: info.n, lastAt: info.last }, label: `Alarm · ${plural(info.n, 'alarm')}` });
    });

  // (b) cameras the agent cited
  for (const [id, via] of aiCitedCameraIds(ev, panel)) {
    const cam = byId.get(id);
    if (!cam || used.has(id)) continue;
    used.add(id);
    out.push({ cam, why: { kind: 'ai', via }, label: 'AI cited' });
  }

  // (c) map neighbours by distance from the first alarm's camera, (d) the rest
  const first = ev.alarms[0]?.cameraId ? byId.get(ev.alarms[0].cameraId) : undefined;
  const of = camShort(first?.name ?? ev.primaryCameraName);
  const rest = ev.cameras.filter((c) => !used.has(c.id)).sort((x, y) => x.distanceRank - y.distanceRank);
  let nth = 0;
  for (const cam of rest) {
    if (cam.distancePx === null) continue;
    nth += 1;
    used.add(cam.id);
    out.push({ cam, why: { kind: 'near', of, nth }, label: `Near ${of} · ${ordinal(nth)}` });
  }
  for (const cam of rest) {
    if (used.has(cam.id)) continue;
    used.add(cam.id);
    out.push({ cam, why: { kind: 'rest' }, label: 'Site camera · not on map' });
  }
  return out;
}

/**
 * The quad (2026-10-28, the user: "the 4 cameras associated with the last 4 alarms"): the cameras of the event's most
 * recent alarms, at most four, and no AI-cited or nearby cameras. The main camera always shows: when it is not one of
 * them it takes the last slot. `prev` (the cameras shown now, by slot) keeps each camera in its slot, so a new alarm's
 * camera takes the slot of the one that dropped out and the other tiles play on; making a tile the main camera doesn't
 * move the tiles either.
 */
export function quadCameras(ranked: RankedCam[], mainId: string | null, prev: readonly string[] = []): EventCamera[] {
  const top = ranked.filter((r) => r.why.kind === 'alarm').slice(0, 4).map((r) => r.cam);
  if (mainId && !top.some((c) => c.id === mainId)) {
    const main = ranked.find((r) => r.cam.id === mainId)?.cam;
    if (main) top.splice(Math.min(top.length, 3), 1, main);
  }
  return keepSlots(top, prev);
}

/** `next` reordered so each camera that is also in `prev` keeps its slot; the others fill the free slots in order. */
export function keepSlots<T extends { id: string }>(next: readonly T[], prev: readonly string[]): T[] {
  const slots: (T | undefined)[] = next.map((_, i) => next.find((c) => c.id === prev[i]));
  const rest = next.filter((c) => !slots.includes(c));
  return slots.map((c) => c ?? rest.shift()!);
}
